<script setup>
// Contador "Por Facturar" (Victor 2026-06-09 · rediseño P2 2026-06-10).
// Badge flotante 🧾 con globo numérico = clientes con baucher/depósito detectado
// HOY sin facturar. Visible SOLO para 3 cuentas (el backend filtra). El modal
// agrupa por sucursal con anillo facturados/total (estilo el sistema de
// facturación de Victor). Clic en el cliente abre su CHAT; "Factura realizada"
// (no antes) lo saca de la lista. Refresca cada 45s.
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import PorFacturarAPI from 'dashboard/api/porFacturar';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';

const store = useStore();
const router = useRouter();

const stats = ref(null);
const open = ref(false);
// Filtros (Victor 2026-06-12): today · yesterday · rango · pending · confirmed.
// Por defecto abre en "Pendientes" (acumulado sin facturar) — es lo accionable.
const activeTab = ref('pending');
// Contexto de fecha para Confirmadas: 'today' | 'yesterday' | 'range'. Pendientes
// y Confirmadas NO lo cambian; solo Hoy/Ayer/Rango. Así Confirmadas muestra las
// del periodo activo (Hoy→confirmadas de hoy, Ayer→de ayer, Rango→del rango).
const datePeriod = ref('today');
const fromDate = ref('');
const toDate = ref('');
const billingId = ref(null);
const collapsed = ref({});
let timer = null;

const allowed = computed(() => stats.value?.allowed === true);
const total = computed(() => stats.value?.total ?? 0);
const yesterdayTotal = computed(() => stats.value?.yesterday_total ?? 0);
const pendingTotal = computed(() => stats.value?.pending_total ?? 0);
// Confirmadas según el periodo de fecha activo (hoy / ayer / rango).
const confirmedTotal = computed(() => {
  const s = stats.value || {};
  if (datePeriod.value === 'yesterday') return s.confirmed_yesterday_total ?? 0;
  if (datePeriod.value === 'range') return s.confirmed_range_total ?? 0;
  return s.confirmed_today_total ?? 0;
});
const groups = computed(() => {
  const s = stats.value || {};
  switch (activeTab.value) {
    case 'yesterday':
      return s.yesterday || [];
    case 'pending':
      return s.pending || [];
    case 'confirmed':
      if (datePeriod.value === 'yesterday') return s.confirmed_yesterday || [];
      if (datePeriod.value === 'range') return s.confirmed_range || [];
      return s.confirmed_today || [];
    case 'rango':
      return s.range || [];
    default:
      return s.today || [];
  }
});
const isEmpty = computed(() => groups.value.length === 0);
const emptyText = computed(() => {
  switch (activeTab.value) {
    case 'yesterday':
      return 'No hubo comprobantes ayer sin facturar.';
    case 'pending':
      return 'No hay clientes pendientes de facturar.';
    case 'confirmed':
      return datePeriod.value === 'yesterday'
        ? 'No hay confirmadas de ayer.'
        : datePeriod.value === 'range'
          ? 'No hay confirmadas en ese rango.'
          : 'Aún no hay confirmadas de hoy.';
    case 'rango':
      return fromDate.value && toDate.value
        ? 'Sin comprobantes en ese rango.'
        : 'Elige un rango de fechas.';
    default:
      return 'Aún nadie ha enviado comprobante hoy.';
  }
});

function selectTab(t) {
  activeTab.value = t;
  // Solo Hoy/Ayer/Rango cambian el periodo de fecha; Pendientes/Confirmadas lo respetan.
  if (t === 'today') datePeriod.value = 'today';
  else if (t === 'yesterday') datePeriod.value = 'yesterday';
  else if (t === 'rango') datePeriod.value = 'range';
  if (t === 'rango' && fromDate.value && toDate.value) load();
}
function applyRange() {
  if (fromDate.value && toDate.value) load();
}

// Colores del sistema de facturación de Victor (por sucursal).
const BRANCH_COLORS = { 2: '#ec4899', 3: '#3b82f6', 5: '#22c55e', 9: '#f59e0b', 4: '#f59e0b' };
function branchColor(id) {
  return BRANCH_COLORS[id] || '#8a8a90';
}
function pct(g) {
  return g.total ? Math.round((g.billed / g.total) * 100) : 0;
}
function ringOffset(g) {
  const circ = 88; // 2·π·14
  return circ - (circ * pct(g)) / 100;
}

function isExpanded(id) {
  return !collapsed.value[id];
}
function toggleExpand(id) {
  collapsed.value = { ...collapsed.value, [id]: !collapsed.value[id] };
}

async function load() {
  try {
    const useRange = datePeriod.value === 'range' && fromDate.value && toDate.value;
    const { data } = await PorFacturarAPI.stats(
      useRange ? fromDate.value : null,
      useRange ? toDate.value : null
    );
    stats.value = data;
  } catch (e) {
    /* silencioso */
  }
}

function toggle() {
  open.value = !open.value;
  closeCtxMenu();
  if (open.value) load();
}

// Clic en el cliente → abre su CHAT (toda la conversación), igual que en Leads.
function chatUrl(it) {
  const accountId = store.getters.getCurrentAccountId;
  return frontendURL(conversationUrl({ accountId, id: it.conversation_id }));
}
function openChat(it) {
  if (!it.conversation_id) return;
  open.value = false;
  router.push(chatUrl(it));
}

// Clic DERECHO en la fila (Victor 2026-06-12): menú propio con "Abrir en nueva
// pestaña" → abre el chat de ese cliente en otra pestaña del navegador sin cerrar
// el modal Por Facturar. Reemplaza el menú nativo de Chrome.
const ctxMenu = ref({ visible: false, x: 0, y: 0, item: null });
function onRowContext(e, it) {
  if (!it.conversation_id) return;
  ctxMenu.value = { visible: true, x: e.clientX, y: e.clientY, item: it };
}
function closeCtxMenu() {
  ctxMenu.value = { ...ctxMenu.value, visible: false, item: null };
}
function openChatNewTab() {
  const it = ctxMenu.value.item;
  closeCtxMenu();
  if (!it?.conversation_id) return;
  // Abrir en SEGUNDO PLANO (Victor 2026-06-12): simulamos un clic con Cmd/Ctrl
  // sobre un <a target="_blank"> → el navegador abre la pestaña sin sacar a
  // Victor del modal, así puede abrir varios clientes seguidos sin perder el hilo.
  const a = document.createElement('a');
  a.href = chatUrl(it);
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.dispatchEvent(
    new MouseEvent('click', {
      bubbles: false,
      cancelable: true,
      view: window,
      ctrlKey: true,
      metaKey: true,
    })
  );
}

// "Factura realizada": estampa billed_at → sube el anillo y saca al cliente.
async function markBilled(it) {
  if (billingId.value) return;
  billingId.value = it.id;
  try {
    const { data } = await PorFacturarAPI.markBilled(it.id);
    stats.value = data;
  } catch (e) {
    /* silencioso */
  } finally {
    billingId.value = null;
  }
}

// Fecha + hora EXACTA del pago detectado, en hora de República Dominicana
// (Victor 2026-06-12). Ej: "12 jun 2026, 12:06 p. m.".
function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Santo_Domingo',
    });
  } catch (e) {
    return '';
  }
}

// Monto a mostrar: si el cliente mandó varios comprobantes, "N comprobantes ·
// TOTAL"; si solo uno, el monto tal cual lo leyó la IA.
function amountLabel(it) {
  if ((it.count || 1) > 1) {
    return `${it.count} comprobantes · ${fmtMoney(it.currency, it.total)}`;
  }
  return it.amount || '—';
}
function fmtMoney(currency, n) {
  if (n == null) return '—';
  const v = Number(n).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency || 'RD$'} ${v}`;
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');
}

onMounted(() => {
  load();
  timer = setInterval(load, 45000);
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div v-if="allowed">
    <!-- Badge icono + globo -->
    <button
      type="button"
      class="pf-chip"
      :title="`Por facturar hoy: ${total}`"
      @click="toggle"
    >
      <span class="pf-ic">🧾</span>
      <span v-if="total > 0" class="pf-badge">{{ total > 99 ? '99+' : total }}</span>
    </button>

    <!-- Listado agrupado por sucursal (P2) -->
    <div v-if="open" class="pf-overlay" @click.self="open = false">
      <div class="pf-drill">
        <div class="pf-h">
          <div>
            <h3>Por Facturar</h3>
            <div class="pf-sub">Baucher/depósito detectado · clic abre el chat</div>
          </div>
          <button type="button" class="pf-x" @click="open = false">✕</button>
        </div>

        <div class="pf-tabs">
          <button type="button" class="pf-tab" :class="{ on: activeTab === 'today' }" @click="selectTab('today')">
            Hoy · {{ total }}
          </button>
          <button type="button" class="pf-tab" :class="{ on: activeTab === 'yesterday' }" @click="selectTab('yesterday')">
            Ayer · {{ yesterdayTotal }}
          </button>
          <button type="button" class="pf-tab" :class="{ on: activeTab === 'rango' }" @click="selectTab('rango')">
            📅 Rango
          </button>
          <button type="button" class="pf-tab amber" :class="{ on: activeTab === 'pending' }" @click="selectTab('pending')">
            Pendientes · {{ pendingTotal }}
          </button>
          <button type="button" class="pf-tab" :class="{ on: activeTab === 'confirmed' }" @click="selectTab('confirmed')">
            ✓ Confirmadas · {{ confirmedTotal }}
          </button>
        </div>

        <!-- Selector de rango de fecha (Victor 2026-06-12) -->
        <div v-if="activeTab === 'rango'" class="pf-range">
          <input v-model="fromDate" type="date" class="pf-date" />
          <span class="pf-arrow">→</span>
          <input v-model="toDate" type="date" class="pf-date" />
          <button type="button" class="pf-apply" :disabled="!fromDate || !toDate" @click="applyRange">Aplicar</button>
        </div>

        <div class="pf-rows">
          <div v-if="isEmpty" class="pf-empty">
            {{ emptyText }}
          </div>

          <div v-for="g in groups" :key="g.branch_id || g.name" class="pf-branch">
            <!-- Cabecera de sucursal con anillo -->
            <button type="button" class="pf-bhead" @click="toggleExpand(g.branch_id)">
              <span class="pf-ring">
                <svg width="34" height="34">
                  <circle cx="17" cy="17" r="14" stroke="rgba(255,255,255,.08)" stroke-width="3.5" fill="none" />
                  <circle
                    cx="17" cy="17" r="14" :stroke="branchColor(g.branch_id)" stroke-width="3.5" fill="none"
                    stroke-dasharray="88" :stroke-dashoffset="ringOffset(g)" stroke-linecap="round"
                  />
                </svg>
                <span class="pf-ring-pc" :style="{ color: branchColor(g.branch_id) }">{{ pct(g) }}%</span>
              </span>
              <span class="pf-bmeta">
                <span class="pf-bname">
                  <i class="pf-bdot" :style="{ background: branchColor(g.branch_id) }" />{{ g.name }}
                </span>
                <span class="pf-bsub">{{ g.billed }} de {{ g.total }} facturados</span>
              </span>
              <span class="pf-chev">{{ isExpanded(g.branch_id) ? '▾' : '▸' }}</span>
            </button>

            <!-- Clientes de la sucursal -->
            <div v-if="isExpanded(g.branch_id)" class="pf-items">
              <div v-for="it in g.items" :key="it.id" class="pf-row" @click="openChat(it)" @contextmenu.prevent="onRowContext($event, it)">
                <span class="pf-av" :style="{ background: branchColor(it.branch_id) }">{{ initials(it.contact_name) }}</span>
                <div class="pf-meta">
                  <span class="pf-name">{{ it.contact_name || 'Sin nombre' }}</span>
                  <span class="pf-dept">{{ it.department }} · {{ amountLabel(it) }} · {{ fmtDateTime(it.at) }}</span>
                </div>
                <span v-if="it.billed" class="pf-confirmed" title="Transferencia confirmada (factura realizada)">✓ Confirmada</span>
                <button
                  v-else
                  type="button"
                  class="pf-bill"
                  :disabled="billingId === it.id"
                  title="Marcar como facturado (lo saca de la lista)"
                  @click.stop="markBilled(it)"
                >{{ billingId === it.id ? '…' : '✓ Factura realizada' }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="pf-note">
          El conteo marca a <b>todos</b> los clientes con pago detectado hoy. El lead solo
          se mueve a <b>“Por confirmar + pendiente facturar”</b> en DTO. DE VENTAS; en los
          demás departamentos se marca sin moverse. <b>Clic en el cliente abre su chat</b>;
          solo <b>“Factura realizada”</b> lo saca de la lista.
        </div>
      </div>

      <!-- Menú clic derecho (Victor 2026-06-12): "Abrir en nueva pestaña". El
           backdrop transparente captura el clic para cerrarlo. -->
      <template v-if="ctxMenu.visible">
        <div
          class="pf-ctx-backdrop"
          @click="closeCtxMenu"
          @contextmenu.prevent="closeCtxMenu"
        />
        <div class="pf-ctx" :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }">
          <button type="button" class="pf-ctx-item" @click="openChatNewTab">
            <span class="pf-ctx-ico">↗</span> Abrir en nueva pestaña
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.pf-chip {
  position: fixed;
  top: 12px;
  right: 104px;
  z-index: 7000;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid rgba(63, 191, 143, 0.4);
  background: rgba(63, 191, 143, 0.08);
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 15px;
  transition: transform 0.16s ease;
}
.pf-chip:hover {
  transform: scale(1.08);
  background: rgba(63, 191, 143, 0.16);
}
.pf-ic {
  line-height: 1;
}
.pf-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 99px;
  background: #3fbf8f;
  color: #06231a;
  font-size: 10px;
  font-weight: 800;
  display: grid;
  place-items: center;
}

.pf-overlay {
  position: fixed;
  inset: 0;
  z-index: 7001;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.pf-drill {
  width: 100%;
  max-width: 540px;
  border: 1px solid rgba(63, 191, 143, 0.26);
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(19, 17, 20, 0.98), rgba(11, 11, 13, 0.99));
  box-shadow: 0 50px 130px rgba(0, 0, 0, 0.7);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
}
.pf-h {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.pf-h h3 {
  font-size: 15px;
  font-weight: 500;
  color: #f4f4f5;
}
.pf-sub {
  font-size: 11px;
  color: #6c6c70;
  font-weight: 300;
  margin-top: 3px;
}
.pf-x {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #6c6c70;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
}
.pf-x:hover {
  color: #f4f4f5;
}
.pf-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 13px 22px 4px;
}
.pf-tab {
  font-size: 11px;
  color: #9a9a9f;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.025);
  font-weight: 300;
  cursor: pointer;
  font-family: inherit;
  transition: 0.16s;
}
.pf-tab.on {
  background: rgba(63, 191, 143, 0.1);
  border-color: rgba(63, 191, 143, 0.26);
  color: #7fe3c0;
  font-weight: 600;
}
.pf-tab.amber.on {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: #fbbf24;
}

/* Selector de rango de fecha (Victor 2026-06-12) */
.pf-range {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 22px 2px;
}
.pf-date {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #e7e9ec;
  padding: 6px 9px;
  font-size: 11px;
  font-family: inherit;
  color-scheme: dark;
}
.pf-arrow {
  color: #6c6c70;
  font-size: 12px;
}
.pf-apply {
  padding: 6px 13px;
  border-radius: 8px;
  background: #3fbf8f;
  color: #06231a;
  font-weight: 700;
  font-size: 11px;
  border: 0;
  cursor: pointer;
  font-family: inherit;
}
.pf-apply:disabled {
  opacity: 0.4;
  cursor: default;
}
.pf-confirmed {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: #06231a;
  background: #3fbf8f;
  padding: 6px 10px;
  border-radius: 8px;
  white-space: nowrap;
}
.pf-rows {
  padding: 8px 14px 14px;
  overflow: auto;
}
.pf-empty {
  font-size: 11.5px;
  color: #6c6c70;
  font-style: italic;
  padding: 24px 4px;
  text-align: center;
}
.pf-branch {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.pf-branch:first-child {
  border-top: 0;
}
.pf-bhead {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 8px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.pf-bhead:hover {
  background: rgba(255, 255, 255, 0.02);
}
.pf-ring {
  position: relative;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
}
.pf-ring svg {
  transform: rotate(-90deg);
  display: block;
}
.pf-ring-pc {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: 9px;
  font-weight: 700;
}
.pf-bmeta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.pf-bname {
  font-size: 12.5px;
  font-weight: 700;
  color: #f4f4f5;
  display: flex;
  align-items: center;
  gap: 7px;
}
.pf-bdot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.pf-bsub {
  font-size: 10px;
  color: #6c6c70;
  margin-top: 1px;
}
.pf-chev {
  color: #6c6c70;
  font-size: 11px;
}
.pf-items {
  padding: 0 4px 8px;
}
.pf-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 11px;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.12s;
}
.pf-row:hover {
  background: rgba(255, 255, 255, 0.035);
}
.pf-ctx-backdrop {
  position: fixed;
  inset: 0;
  z-index: 7010;
}
.pf-ctx {
  position: fixed;
  z-index: 7011;
  min-width: 206px;
  padding: 5px;
  border-radius: 11px;
  background: rgba(18, 18, 20, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
}
.pf-ctx-item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 9px 11px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: #f4f4f5;
  text-align: left;
  transition: 0.12s;
}
.pf-ctx-item:hover {
  background: rgba(201, 163, 90, 0.16);
  color: #e8c98a;
}
.pf-ctx-ico {
  font-size: 13px;
  color: #c9a35a;
}
.pf-av {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.pf-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.pf-name {
  font-size: 12.5px;
  font-weight: 500;
  color: #f4f4f5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pf-dept {
  font-size: 10px;
  color: #6c6c70;
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pf-bill {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: #7ee29a;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(34, 197, 94, 0.4);
  background: rgba(34, 197, 94, 0.1);
  cursor: pointer;
  font-family: inherit;
  transition: 0.16s;
  white-space: nowrap;
}
.pf-bill:hover {
  background: rgba(34, 197, 94, 0.2);
}
.pf-bill:disabled {
  opacity: 0.5;
  cursor: default;
}
.pf-note {
  margin: 4px 22px 18px;
  font-size: 10.5px;
  font-weight: 300;
  color: #6c6c70;
  border: 1px solid rgba(63, 191, 143, 0.22);
  background: rgba(63, 191, 143, 0.05);
  border-radius: 10px;
  padding: 10px 13px;
  line-height: 1.55;
}
.pf-note b {
  color: #3fbf8f;
  font-weight: 600;
}
</style>
